import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, DollarSign, Clock, Cpu, FileAudio } from "lucide-react";
import { VendaUsoIa } from "@/lib/vendaAssistant";

type AdminCustosState = {
  loading: boolean;
  usos: VendaUsoIa[];
  usuariosMap: Record<string, string>;
  sessoesMap: Record<string, string>;
  empresaIdFilter: string | null;
};

interface AuxiliarVendaCustosAdminProps {
  empresaId?: string;
}

export function AuxiliarVendaCustosAdmin({ empresaId }: AuxiliarVendaCustosAdminProps) {
  const [state, setState] = useState<AdminCustosState>({
    loading: true,
    usos: [],
    usuariosMap: {},
    sessoesMap: {},
    empresaIdFilter: empresaId || null,
  });

  useEffect(() => {
    const loadData = async () => {
      setState((prev) => ({ ...prev, loading: true }));

      // Consulta 1: Tabela venda_uso_ia
      let query = (supabase as any)
        .from("venda_uso_ia")
        .select("*")
        .order("created_at", { ascending: false });

      if (empresaId) {
        query = query.eq("empresa_id", empresaId);
      }

      const { data: usoData, error } = await query;
      if (error) {
        console.error("Erro ao carregar venda_uso_ia:", error);
        setState((prev) => ({ ...prev, loading: false }));
        return;
      }

      const listaUsos = (usoData || []) as VendaUsoIa[];
      const userIds = Array.from(new Set(listaUsos.map((u) => u.user_id)));
      const sessaoIds = Array.from(new Set(listaUsos.map((u) => u.sessao_id)));

      // Consulta 2 (separada, sem JOIN): Nomes dos usuários
      const uMap: Record<string, string> = {};
      if (userIds.length > 0) {
        const { data: userRecords } = await (supabase as any)
          .from("usuarios")
          .select("id, nome, email")
          .in("id", userIds);

        (userRecords || []).forEach((u: any) => {
          uMap[u.id] = u.nome || u.email;
        });
      }

      // Consulta 3 (separada, sem JOIN): Nomes das sessões/clientes
      const sMap: Record<string, string> = {};
      if (sessaoIds.length > 0) {
        const { data: sessaoRecords } = await (supabase as any)
          .from("venda_sessoes")
          .select("id, cliente_nome")
          .in("id", sessaoIds);

        (sessaoRecords || []).forEach((s: any) => {
          sMap[s.id] = s.cliente_nome;
        });
      }

      setState({
        loading: false,
        usos: listaUsos,
        usuariosMap: uMap,
        sessoesMap: sMap,
        empresaIdFilter: empresaId || null,
      });
    };

    loadData();
  }, [empresaId]);

  if (state.loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Totais agregados
  const totalCustoUsd = state.usos.reduce((acc, curr) => acc + (Number(curr.cost_usd) || 0), 0);
  const totalInputTokens = state.usos.reduce((acc, curr) => acc + (curr.input_tokens || 0), 0);
  const totalOutputTokens = state.usos.reduce((acc, curr) => acc + (curr.output_tokens || 0), 0);
  const totalMinutosAudio = state.usos.reduce((acc, curr) => acc + (Number(curr.minutos_audio) || 0), 0);
  const mediaCustoPorMinuto = totalMinutosAudio > 0 ? totalCustoUsd / totalMinutosAudio : 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-emerald-500" />
              Custo Estimado (USD)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <p className="text-2xl font-bold text-emerald-600">${totalCustoUsd.toFixed(4)}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Média: ${mediaCustoPorMinuto.toFixed(4)} / min
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <FileAudio className="h-4 w-4 text-sky-500" />
              Minutos de Áudio
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <p className="text-2xl font-bold">{totalMinutosAudio.toFixed(1)} min</p>
            <p className="text-xs text-muted-foreground mt-1">Processados via transcrição</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Cpu className="h-4 w-4 text-indigo-500" />
              Tokens Utilizados
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <p className="text-2xl font-bold">{(totalInputTokens + totalOutputTokens).toLocaleString('pt-BR')}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Entrada: {totalInputTokens.toLocaleString()} | Saída: {totalOutputTokens.toLocaleString()}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-500" />
              Requisições Executadas
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <p className="text-2xl font-bold">{state.usos.length}</p>
            <p className="text-xs text-muted-foreground mt-1">Transcrições e análises</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Detalhamento de Uso de IA - Auxiliar de Venda</CardTitle>
          <CardDescription>
            Registro de cada transcrição e análise executada para precificação futura.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data/Hora</TableHead>
                  <TableHead>Usuário</TableHead>
                  <TableHead>Cliente/Sessão</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Modelo</TableHead>
                  <TableHead>Duração (ms)</TableHead>
                  <TableHead>Áudio (min)</TableHead>
                  <TableHead>Tokens</TableHead>
                  <TableHead className="text-right">Custo (USD)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {state.usos.slice(0, 50).map((uso) => (
                  <TableRow key={uso.id}>
                    <TableCell className="text-xs whitespace-nowrap">
                      {new Date(uso.created_at).toLocaleString('pt-BR')}
                    </TableCell>
                    <TableCell className="text-xs font-medium">
                      {state.usuariosMap[uso.user_id] || uso.user_id.slice(0, 8)}
                    </TableCell>
                    <TableCell className="text-xs">
                      {state.sessoesMap[uso.sessao_id] || uso.sessao_id.slice(0, 8)}
                    </TableCell>
                    <TableCell className="text-xs">
                      <Badge variant={uso.tipo === 'transcricao' ? 'outline' : 'secondary'}>
                        {uso.tipo}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground font-mono">
                      {uso.model}
                    </TableCell>
                    <TableCell className="text-xs">
                      {uso.duracao_ms ? `${uso.duracao_ms} ms` : '-'}
                    </TableCell>
                    <TableCell className="text-xs">
                      {uso.minutos_audio ? `${Number(uso.minutos_audio).toFixed(1)} min` : '-'}
                    </TableCell>
                    <TableCell className="text-xs">
                      {uso.input_tokens + uso.output_tokens}
                    </TableCell>
                    <TableCell className="text-xs text-right font-medium text-emerald-600">
                      ${Number(uso.cost_usd).toFixed(5)}
                    </TableCell>
                  </TableRow>
                ))}
                {!state.usos.length && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      Nenhum registro de uso do Auxiliar de Venda encontrado.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
